import { useEffect } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import {
  ArrowLeft,
  CheckCircle2,
  Copy,
  Hash,
  Mail,
  Phone,
  Sparkles,
} from "lucide-react";
import { toast } from "sonner";
import { Header } from "../components/Header";
import { Footer } from "../components/Footer";
import { useLang } from "../context/LanguageContext";

const STRINGS = {
  pt: {
    back: "Voltar a Carreiras",
    overline: "Candidatura recebida",
    title: "Obrigado! Recebemos a sua candidatura.",
    intro:
      "A nossa equipa de Recursos Humanos vai analisar o seu perfil. Guarde o número de referência abaixo — pode usá-lo em qualquer comunicação connosco.",
    ref_label: "Número de referência",
    copy: "Copiar",
    copied: "Referência copiada.",
    next_title: "Próximos passos",
    next_items: [
      "Vamos confirmar a receção por email nas próximas 24h.",
      "Se o seu perfil corresponder à vaga, contactamo-lo para uma entrevista (online ou presencial em Luanda) em 5–10 dias úteis.",
      "Pode acompanhar o seu processo respondendo ao email com a sua referência.",
      "Mesmo que esta vaga não avance, o seu CV fica na nossa base para oportunidades futuras (12 meses).",
    ],
    contact_title: "Em caso de dúvida",
    cta_home: "Ir para o site",
    cta_more: "Outras vagas",
  },
  en: {
    back: "Back to Careers",
    overline: "Application received",
    title: "Thank you! We've received your application.",
    intro:
      "Our HR team will review your profile. Keep the reference number below — you can use it in any communication with us.",
    ref_label: "Reference number",
    copy: "Copy",
    copied: "Reference copied.",
    next_title: "Next steps",
    next_items: [
      "We'll confirm receipt by email within the next 24h.",
      "If your profile matches the role, we'll reach out for an interview (online or in person in Luanda) within 5–10 business days.",
      "You can follow up by replying to that email with your reference.",
      "Even if this role doesn't move forward, your CV stays in our database for future openings (12 months).",
    ],
    contact_title: "Any questions?",
    cta_home: "Go to website",
    cta_more: "Other openings",
  },
  es: {
    back: "Volver a Carreras",
    overline: "Candidatura recibida",
    title: "¡Gracias! Hemos recibido tu candidatura.",
    intro:
      "Nuestro equipo de RR.HH. revisará tu perfil. Guarda el número de referencia abajo — puedes usarlo en cualquier comunicación con nosotros.",
    ref_label: "Número de referencia",
    copy: "Copiar",
    copied: "Referencia copiada.",
    next_title: "Próximos pasos",
    next_items: [
      "Confirmaremos la recepción por email en las próximas 24h.",
      "Si tu perfil encaja con la vacante, contactaremos para una entrevista (online o presencial en Luanda) en 5–10 días hábiles.",
      "Puedes hacer seguimiento respondiendo a ese email con tu referencia.",
      "Incluso si esta vacante no avanza, tu CV permanece en nuestra base para oportunidades futuras (12 meses).",
    ],
    contact_title: "¿Alguna duda?",
    cta_home: "Ir al sitio",
    cta_more: "Otras vacantes",
  },
};

export default function CareersSuccess() {
  const { lang } = useLang();
  const location = useLocation();
  const navigate = useNavigate();
  const s = STRINGS[lang] || STRINGS.pt;

  // Reference comes from router state (set during navigate after submit) OR
  // from the query string (so the URL is shareable & survives refresh).
  const stateRef = location.state?.reference;
  const queryRef = new URLSearchParams(location.search).get("ref");
  const reference = stateRef || queryRef || "";

  // If someone lands here without a reference, send them back to the form.
  useEffect(() => {
    if (!reference) navigate("/carreiras", { replace: true });
  }, [reference, navigate]);

  const copyRef = async () => {
    try {
      await navigator.clipboard.writeText(reference);
      toast.success(s.copied);
    } catch {
      // Clipboard API unavailable (e.g. insecure context) — silently noop.
    }
  };

  if (!reference) return null;

  return (
    <div className="min-h-screen bg-white">
      <Header />

      <section className="relative overflow-hidden bg-gradient-to-b from-emerald-50 via-white to-white pb-20 pt-28 md:pt-32">
        <div
          aria-hidden
          className="pointer-events-none absolute -top-32 right-1/4 h-[420px] w-[420px] rounded-full bg-emerald-200/40 blur-3xl"
        />
        <div className="relative mx-auto max-w-3xl px-6 md:px-12">
          <Link
            to="/carreiras"
            data-testid="careers-success-back"
            className="inline-flex items-center gap-2 text-[12px] font-semibold uppercase tracking-[0.18em] text-slate-500 transition-colors hover:text-slate-900"
          >
            <ArrowLeft className="h-3.5 w-3.5" strokeWidth={2.5} />
            {s.back}
          </Link>

          <div className="mt-8 flex items-center gap-3">
            <div className="inline-flex h-14 w-14 shrink-0 items-center justify-center rounded-full bg-emerald-500 text-white shadow-lg shadow-emerald-500/30">
              <CheckCircle2 className="h-8 w-8" strokeWidth={2.5} />
            </div>
            <div>
              <div className="font-mono text-[11px] uppercase tracking-[0.28em] text-emerald-700">
                {s.overline}
              </div>
              <h1
                data-testid="careers-success-title"
                className="font-heading mt-1 text-3xl font-black tracking-tight text-slate-900 md:text-4xl"
              >
                {s.title}
              </h1>
            </div>
          </div>

          <p className="mt-5 max-w-2xl text-sm leading-relaxed text-slate-600 md:text-base">
            {s.intro}
          </p>

          {/* Reference badge */}
          <div className="mt-8 rounded-3xl border border-emerald-300 bg-white p-6 shadow-xl md:p-8">
            <div className="flex flex-col items-start gap-4 md:flex-row md:items-center md:justify-between">
              <div>
                <div className="flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.22em] text-emerald-700">
                  <Hash className="h-3 w-3" strokeWidth={2.5} />
                  {s.ref_label}
                </div>
                <div
                  data-testid="careers-success-reference"
                  className="font-heading mt-2 text-3xl font-black tracking-tight text-slate-900 md:text-4xl"
                >
                  {reference}
                </div>
              </div>
              <button
                type="button"
                onClick={copyRef}
                data-testid="careers-success-copy"
                className="inline-flex items-center gap-2 rounded-full border border-emerald-500 bg-emerald-500 px-5 py-2.5 text-sm font-bold text-white shadow-md transition-all hover:-translate-y-0.5 hover:bg-emerald-600"
              >
                <Copy className="h-4 w-4" strokeWidth={2.5} />
                {s.copy}
              </button>
            </div>
          </div>

          {/* Next steps */}
          <div className="mt-8 rounded-3xl border border-slate-200 bg-white p-6 md:p-8">
            <div className="flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.22em] text-blue-700">
              <Sparkles className="h-3 w-3" strokeWidth={2.5} />
              {s.next_title}
            </div>
            <ol className="mt-4 space-y-3.5">
              {s.next_items.map((it, i) => (
                <li
                  key={it}
                  className="flex items-start gap-3 text-sm leading-relaxed text-slate-700 md:text-[15px]"
                >
                  <span className="inline-flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-blue-100 text-[12px] font-bold text-blue-700">
                    {i + 1}
                  </span>
                  <span>{it}</span>
                </li>
              ))}
            </ol>
          </div>

          {/* Contact strip */}
          <div className="mt-8 rounded-3xl border border-slate-200 bg-slate-50 p-6 md:p-7">
            <div className="font-mono text-[10px] uppercase tracking-[0.22em] text-slate-500">
              {s.contact_title}
            </div>
            <div className="mt-3 flex flex-wrap gap-x-6 gap-y-2 text-sm">
              <a
                href="mailto:rh@tvcabsat.ao"
                className="inline-flex items-center gap-2 font-medium text-slate-800 transition-colors hover:text-blue-700"
              >
                <Mail className="h-4 w-4" strokeWidth={2.2} />
                rh@tvcabsat.ao
              </a>
              <a
                href="tel:+244925687036"
                className="inline-flex items-center gap-2 font-medium text-slate-800 transition-colors hover:text-blue-700"
              >
                <Phone className="h-4 w-4" strokeWidth={2.2} />
                +244 925 687 036
              </a>
            </div>
          </div>

          {/* CTAs */}
          <div className="mt-10 flex flex-wrap gap-3">
            <Link
              to="/"
              data-testid="careers-success-home"
              className="inline-flex items-center gap-2 rounded-full bg-slate-900 px-6 py-3 text-sm font-bold text-white shadow-md transition-all hover:-translate-y-0.5 hover:bg-slate-800"
            >
              {s.cta_home}
            </Link>
            <Link
              to="/carreiras"
              data-testid="careers-success-more"
              className="inline-flex items-center gap-2 rounded-full border border-slate-300 bg-white px-6 py-3 text-sm font-semibold text-slate-800 transition-all hover:-translate-y-0.5 hover:border-blue-500 hover:text-blue-700"
            >
              {s.cta_more}
            </Link>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
